import React, { useState, useEffect } from 'react';
import * as Styles from './ConfirmationOpenClawToken.styles';
import useLocale from '@/hooks/useLocale';
import { useSelector, useDispatch } from 'react-redux';
import { vpsOciActions } from '@/redux/modules/productDetail/vpsOci';
import {
  AlertV2, Button, TextField, Modal,
} from 'gatorcomponents';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useParams } from 'react-router-dom';

const ConfirmationOpenClawToken = ({
  onShowConfigurationOpenClaw,
}) => {
  const { id: hostingId } = useParams();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const [showModal, setShowModal] = useState(false);
  const openClawSagas = useSelector(state => state.vpsOci.openClaw);
  const { success: openClawSuccess, loading: openClawLoading } = openClawSagas || {};

  const { productDetails: productDetailsLocale } = useLocale();
  const { vpsOci: vpsOciLocale } = productDetailsLocale || {};
  const openClawConfirmationOpenClawTokenPageLocale = (vpsOciLocale && vpsOciLocale.openClaw && vpsOciLocale.openClaw.confirmationTokenPage) || {};

  const {
    title: titleLocale,
    description: descriptionLocale,
    inputs: {
      openai: {
        placeholder: {
          desktop: openaiPlaceholderDesktop,
          mobile: openaiPlaceholderMobile,
        } = {},
        hint: openaiHintLocale,
      } = {},
    } = {},
    observation: observationLocale,
    buttons: {
      confirm: confirmButtonLocale,
      manualSetupLater: manualSetupLaterButtonLocale,
    } = {},
    modal: {
      title: modalTitleLocale,
      description: modalDescriptionLocale,
      buttons: {
        manualSetupLater: modalManualSetupLaterButtonLocale,
        configureNow: modalConfigureNowButtonLocale,
      } = {},
    } = {},
  } = openClawConfirmationOpenClawTokenPageLocale;

  const validationConfig = {
    mode: 'any', // 'all' | 'any' | 'specific' | 'none'
  };

  const tokenOptions = [
    {
      id: 'openai',
      label: {
        mobile: openaiPlaceholderMobile,
        desktop: openaiPlaceholderDesktop,
      },
      hint: openaiHintLocale,
      minLength: 100,
      required: true, // Define se este token é obrigatório quando mode = 'specific'
    },
  ];

  const [inputValues, setInputValues] = useState({
    openclaw: {
      tokens: tokenOptions.reduce((acc, option) => ({
        ...acc,
        [option.id]: '',
      }), {}),
    },
  });

  const validateToken = option => (
    inputValues
      && inputValues.openclaw
      && inputValues.openclaw.tokens
      && inputValues.openclaw.tokens[option.id]
      && inputValues.openclaw.tokens[option.id].length >= option.minLength
  );

  const isValid = (() => {
    switch (validationConfig.mode) {
      case 'none':
        return true;

      case 'any':
        return tokenOptions.some(option => validateToken(option));

      case 'specific': {
        const requiredOptions = tokenOptions.filter(opt => opt.required);
        if (requiredOptions.length === 0) {
          return true;
        }
        return requiredOptions.every(option => validateToken(option));
      }

      case 'all':
      default:
        return tokenOptions.every(option => validateToken(option));
    }
  })();

  const handleInputChange = (optionId, value) => {
    setInputValues(prev => ({
      ...prev,
      openclaw: {
        ...prev.openclaw,
        tokens: {
          ...prev.openclaw.tokens,
          [optionId]: value,
        },
      },
    }));
  };

  const handleConfirmSendToken = () => {
    setShowModal(false);
    const data = {
      ...inputValues,
    };
    dispatch(vpsOciActions.openClaw.saveToken(hostingId, data));
  };

  const handleManualSetupLater = () => {
    setShowModal(true);
  };

  const handleModalNoConfigSetup = () => {
    setShowModal(false);
    dispatch(vpsOciActions.openClaw.saveToken(hostingId, null));
    onShowConfigurationOpenClaw && onShowConfigurationOpenClaw(false);
  };

  const handleModalConfigSetupNow = () => {
    setShowModal(false);
  };

  useEffect(() => {
    if (openClawSuccess) {
      onShowConfigurationOpenClaw && onShowConfigurationOpenClaw(false);
    }
  }, [openClawSuccess, onShowConfigurationOpenClaw]);

  return (
    <Styles.ConfirmationOpenClawToken data-testid="vpsoci-confirmation-open-claw-token-page">
      <Styles.Wrapper>
        <Styles.Title data-testid="vpsoci-confirmation-open-claw-token-title">{titleLocale}</Styles.Title>
        <Styles.Description data-testid="vpsoci-confirmation-open-claw-token-description">
          {descriptionLocale}
        </Styles.Description>
        <Styles.InputWrapper data-testid="vpsoci-confirmation-open-claw-token-input-wrapper">
          {tokenOptions.map(option => (
            <TextField
              key={option.id}
              label={isMobile ? option.label.mobile : option.label.desktop}
              value={inputValues.openclaw.tokens[option.id]}
              onChange={e => handleInputChange(option.id, e.target.value)}
              fullWidth
              type="password"
              helpText={option.hint}
              testId={`vpsoci-confirmation-open-claw-token-input-${option.id}-token`}
            />
          ))}
        </Styles.InputWrapper>
        <AlertV2
          description={observationLocale}
          variant="info"
          width="full"
          withoutBackground={false}
          testId="vpsoci-confirmation-open-claw-token-observation"
        />
        <Styles.Actions>
          <Button
            label={confirmButtonLocale}
            onClick={handleConfirmSendToken}
            variant="primary"
            size="large"
            width="full"
            disabled={!isValid}
            testId="vpsoci-confirmation-open-claw-token-configure-now-button"
            loading={openClawLoading}
          />
          <Button
            label={manualSetupLaterButtonLocale}
            onClick={handleManualSetupLater}
            variant="tertiary"
            size="large"
            width="full"
            testId="vpsoci-confirmation-open-claw-token-manual-setup-later-button"
          />
        </Styles.Actions>
      </Styles.Wrapper>

      {showModal && (
        <Modal
          onClose={null}
          maxWidth={800}
          title={modalTitleLocale}
          testId="vpsoci-confirmation-open-claw-token-modal"
        >
          <Styles.ModalContainer>
            <Styles.ModalContent>
              <Styles.ModalDescription data-testid="vpsoci-confirmation-open-claw-token-modal-description">
                {modalDescriptionLocale}
              </Styles.ModalDescription>
            </Styles.ModalContent>
            <Styles.ModalActions>
              <Button
                label={modalManualSetupLaterButtonLocale}
                variant="tertiary"
                size="large"
                width="full"
                onClick={handleModalNoConfigSetup}
                testId="vpsoci-confirmation-open-claw-token-modal-manual-setup-later-button"
              />
              <Button
                label={modalConfigureNowButtonLocale}
                variant="primary"
                size="large"
                width="full"
                onClick={handleModalConfigSetupNow}
                testId="vpsoci-confirmation-open-claw-token-modal-configure-now-button"
              />
            </Styles.ModalActions>
          </Styles.ModalContainer>
        </Modal>
      )}
    </Styles.ConfirmationOpenClawToken>
  );
};

export default ConfirmationOpenClawToken;
